"use client";

import { c as _c } from "react/compiler-runtime";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useCallback, useState } from "react";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import WaveDropActions from "../WaveDropActions";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import ParticipationDropContainer from "./ParticipationDropContainer";
import ParticipationDropHeader from "./ParticipationDropHeader";
import ParticipationDropContent from "./ParticipationDropContent";
import ParticipationDropMetadata from "./ParticipationDropMetadata";
import ParticipationDropFooter from "./ParticipationDropFooter";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import ParticipationIdentityProfileCard from "./ParticipationIdentityProfileCard";
import { getParticipationIdentityProfile, getParticipationVisibleMetadata } from "./participationIdentityProfile.helpers";
import type { DropInteractionParams, DropLocation } from "../drop.types";
interface OngoingParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
export default function OngoingParticipationDrop(t0) {
  const $ = _c(71);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick
  } = t0;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const isMobile = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  const hasTouch = useIsTouchDevice() || isMobile;
  let t1;
  if ($[0] !== drop.metadata || $[1] !== drop.wave) {
    t1 = getParticipationIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    $[0] = drop.metadata;
    $[1] = drop.wave;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const identityProfile = t1;
  let t2;
  if ($[3] !== drop.metadata || $[4] !== drop.wave) {
    t2 = getParticipationVisibleMetadata({
      wave: drop.wave,
      metadata: drop.metadata
    });
    $[3] = drop.metadata;
    $[4] = drop.wave;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const visibleMetadata = t2;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  let t3;
  if ($[6] !== hasTouch) {
    t3 = () => {
      if (!hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[6] = hasTouch;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleLongPress = t3;
  let t4;
  if ($[8] !== activePartIndex || $[9] !== drop || $[10] !== onReply) {
    t4 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[8] = activePartIndex;
    $[9] = drop;
    $[10] = onReply;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleOnReply = t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsSlideUp(false);
    };
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleOnAddReaction = t5;
  let t6;
  if ($[13] !== canShowVote || $[14] !== drop) {
    t6 = canShowVote ? <VotingModalButton drop={drop} onClick={() => setIsVotingModalOpen(true)} /> : null;
    $[13] = canShowVote;
    $[14] = drop;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const voteAction = t6;
  let t7;
  if ($[16] !== activePartIndex || $[17] !== drop || $[18] !== handleOnReply || $[19] !== isMobile || $[20] !== showReplyAndQuote) {
    t7 = !isMobile && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} />;
    $[16] = activePartIndex;
    $[17] = drop;
    $[18] = handleOnReply;
    $[19] = isMobile;
    $[20] = showReplyAndQuote;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== drop) {
    t8 = <WaveDropAuthorPfp drop={drop} />;
    $[22] = drop;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== drop || $[25] !== showWaveInfo) {
    t9 = <ParticipationDropHeader drop={drop} showWaveInfo={showWaveInfo} />;
    $[24] = drop;
    $[25] = showWaveInfo;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== activePartIndex || $[28] !== drop || $[29] !== handleLongPress || $[30] !== onDropContentClick || $[31] !== onQuoteClick) {
    t10 = <ParticipationDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} />;
    $[27] = activePartIndex;
    $[28] = drop;
    $[29] = handleLongPress;
    $[30] = onDropContentClick;
    $[31] = onQuoteClick;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t9) {
    t11 = <div className="tw-flex tw-w-full tw-flex-col">{t9}{t10}</div>;
    $[33] = t10;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t11 || $[37] !== t8) {
    t12 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-px-4 tw-pt-4 tw-text-left">{t8}{t11}</div>;
    $[36] = t11;
    $[37] = t8;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== drop.id || $[40] !== identityProfile) {
    t13 = identityProfile && <div className="tw-px-4 sm:tw-ml-[3.25rem]"><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant="chat" /></div>;
    $[39] = drop.id;
    $[40] = identityProfile;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== drop.id || $[43] !== visibleMetadata) {
    t14 = <ParticipationDropMetadata metadata={visibleMetadata} contextId={drop.id} />;
    $[42] = drop.id;
    $[43] = visibleMetadata;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== drop || $[46] !== voteAction) {
    t15 = <ParticipationDropFooter drop={drop} voteAction={voteAction} />;
    $[45] = drop;
    $[46] = voteAction;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== t13 || $[49] !== t14 || $[50] !== t15) {
    t16 = <div className="tw-flex tw-w-full tw-flex-col">{t13}{t14}{t15}</div>;
    $[48] = t13;
    $[49] = t14;
    $[50] = t15;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== drop || $[53] !== isMobileScreen || $[54] !== isVotingModalOpen) {
    t17 = isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />;
    $[52] = drop;
    $[53] = isMobileScreen;
    $[54] = isVotingModalOpen;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== drop || $[57] !== handleOnReply || $[58] !== isSlideUp || $[59] !== longPressTriggered || $[60] !== showReplyAndQuote) {
    t18 = <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[56] = drop;
    $[57] = handleOnReply;
    $[58] = isSlideUp;
    $[59] = longPressTriggered;
    $[60] = showReplyAndQuote;
    $[61] = t18;
  } else {
    t18 = $[61];
  }
  let t19;
  if ($[62] !== drop || $[63] !== isActiveDrop || $[64] !== location || $[65] !== t12 || $[66] !== t16 || $[67] !== t17 || $[68] !== t18 || $[69] !== t7) {
    t19 = <ParticipationDropContainer drop={drop} isActiveDrop={isActiveDrop} location={location}>{t7}{t12}{t16}{t17}{t18}</ParticipationDropContainer>;
    $[62] = drop;
    $[63] = isActiveDrop;
    $[64] = location;
    $[65] = t12;
    $[66] = t16;
    $[67] = t17;
    $[68] = t18;
    $[69] = t7;
    $[70] = t19;
  } else {
    t19 = $[70];
  }
  return t19;
}